import { useState } from "react";

import Seo from "../components/Seo";
import AuthCard from "../components/AuthCard";
import { NewPasswordFields, PasswordInput } from "../components/PasswordFields";
import { passwordReady } from "../utils/passwordRules";
import { Button } from "../components/ui";
import { auth } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { friendlyError } from "../utils/authErrors";

/**
 * The forced password change for a customer login — Method B's first sign-in (CS-5A, R-9).
 *
 * ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────
 * A customer login provisioned with a temporary password carries `mustChangePassword: true`, and
 * `requirePasswordChanged` then answers 403 on every customer read. The portal had no forced screen, so a new
 * customer signed in successfully and met a portal where every card showed an error.
 *
 * ── IT IS RENDERED IN PLACE, NOT ROUTED TO ──────────────────────────────────
 * `ProtectedRoute` returns this instead of the requested page. There is no `/change-password` URL to bookmark
 * and no route to navigate around — every protected path renders this until the flag clears.
 *
 * ── NO RE-LOGIN AFTERWARDS ──────────────────────────────────────────────────
 * CS-5A: the change ends every OTHER session of this customer, and the server re-issues THIS session's cookie
 * in the same response, so `refresh()` alone opens the existing portal (R-10).
 *
 * The new-password block is the SAME component as activation and reset, so the rules read the same everywhere.
 */
export default function ForcePasswordChange() {
  const { user, signOut, refresh } = useAuth();

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  const sameAsCurrent = password.length > 0 && password === current;
  const ready = current.length > 0 && passwordReady(password, confirm, user?.email) && !sameAsCurrent;

  const submit = async (ev) => {
    ev.preventDefault();
    if (saving) return;
    if (!current) {
      setErrors({ currentPassword: "Enter the temporary password VVR gave you" });
      document.getElementById("fpc-current")?.focus();
      return;
    }
    if (!ready) return;
    setFailure("");
    try {
      setSaving(true);
      await auth.changePassword(current, password);
      setCurrent("");
      setPassword("");
      setConfirm("");
      await refresh();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.message === "Current password is incorrect") {
        setErrors({ currentPassword: "The temporary password is not correct" });
      } else {
        setFailure(friendlyError(err, "Your password could not be changed. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Seo title="Create your new password" description="Replace your temporary password to continue." path="/login" />
      <AuthCard icon="bi-shield-lock" title="Create your new password"
        subtitle="Your temporary password must be replaced before you can continue."
        footer={
          <button type="button" className="btn btn-sm btn-link text-muted" disabled={saving} onClick={signOut}>
            Sign out instead
          </button>
        }>
        {failure && <div className="alert alert-danger py-2 small" role="alert">{failure}</div>}
        <form onSubmit={submit} noValidate>
          <PasswordInput
            id="fpc-current"
            label="Temporary password"
            value={current}
            onChange={(e) => { setCurrent(e.target.value); setErrors({}); setFailure(""); }}
            autoComplete="current-password"
            error={errors.currentPassword}
            autoFocus
          />
          <NewPasswordFields
            password={password}
            confirm={confirm}
            onPassword={(e) => { setPassword(e.target.value); setFailure(""); }}
            onConfirm={(e) => { setConfirm(e.target.value); setFailure(""); }}
            email={user?.email}
            errors={sameAsCurrent ? { newPassword: "Your new password must be different from the temporary one" } : {}}
          />
          <Button type="submit" className="w-100" loading={saving} disabled={saving || !ready}>
            {saving ? "Setting password…" : "Set new password"}
          </Button>
          {!ready && <p className="form-text text-center mb-0 mt-2">Complete every requirement above to continue.</p>}
        </form>
      </AuthCard>
    </>
  );
}
